import { CheckboxField, Col, TextInputField } from '@/components';
import { PS_MAX_KEYWORD_SEARCH_LENGTH } from '@/features/project/_utils/constants';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import { shouldShowIndicatorKeywordCheckbox } from '@/features/search/_utils/activity-indicator-conditions';
import { COL_MIN_WIDTH } from '@/features/search/_utils/projects-search';
import { isPS } from '@/utils';
import { type FC, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const ActivityIndicatorKeywordColField: FC = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectSearchFormType>();
  const indicatorType = useWatch({ control, name: 'indicatorType' });
  const indicatorKeywordSearchOnlyTitleAndDescriptionType =
    shouldShowIndicatorKeywordCheckbox(indicatorType);

  const hint = useMemo(() => {
    const suffix = isPS()
      ? t('hint-search-activity-indicator-keyword-suffix-at-public')
      : t('hint-search-activity-indicator-keyword-suffix');

    switch (indicatorType) {
      case 'activity':
        return [
          t('hint-search-activity-indicator-prefix'),
          t('hint-search-activity-indicator-activity'),
          suffix,
        ].join('\n');
      case 'output':
        return [
          t('hint-search-activity-indicator-prefix'),
          t('hint-search-activity-indicator-output', {
            target: t('activities-target'),
            indicator: t('output-indicator'),
          }),
          suffix,
        ].join('\n');
      case 'outcome':
        return [
          t('hint-search-activity-indicator-prefix'),
          t('hint-search-activity-indicator-outcome', {
            target: t('outcome-objective'),
            indicator: t('outcome-indicator'),
          }),
          suffix,
        ].join('\n');
      default:
        return [
          t('hint-search-activity-indicator-prefix'),
          t('hint-search-activity-indicator-default', {
            additionalTarget: isPS() ? '' : `・${t('type')}`,
          }),
          suffix,
        ].join('\n');
    }
  }, [indicatorType, t]);

  return (
    <Col span={8} style={{ minWidth: COL_MIN_WIDTH.large }}>
      <TextInputField
        name="indicatorKeyword"
        label={t('keyword')}
        placeholder={t('placeholder-keyword-partial-match')}
        control={control}
        errors={errors}
        {...(isPS() && { maxLength: PS_MAX_KEYWORD_SEARCH_LENGTH })}
        hint={hint}
        allowClear
        additionalLabelComponent={
          indicatorKeywordSearchOnlyTitleAndDescriptionType.result ===
          'unNeed' ? null : (
            <CheckboxField
              text={t('search-activity-indicator-name')}
              name="indicatorKeywordSearchOnlyTitleAndDescription"
              control={control}
              errors={errors}
              disabled={
                indicatorKeywordSearchOnlyTitleAndDescriptionType.result ===
                'disable'
              }
              wrapperStyle={{
                marginBottom: 0,
                marginLeft: 4,
                height: '22px',
                // .ant-form-item-control-inputのmin-heightを上書きするのが難しいので無理やり位置調整してます。
                transform: 'translateY(-4px)',
              }}
            />
          )
        }
      />
    </Col>
  );
};
